import { FontWeight } from "@/theme/typography";
import { DIMMED_OPACITY, GANTT_STATUS_COLORS } from "./gantt-constants";
import type { GanttTaskInteractionState } from "./gantt-render-selectors";
import type { GanttSectionRowLayout, GanttTaskLayout } from "./gantt-types";

enum GanttLabelHoverState {
  Hovered = "hovered",
  Idle = "idle",
}

enum GanttLabelDimState {
  Dimmed = "dimmed",
  Normal = "normal",
}

enum GanttLabelCollapseState {
  Collapsed = "collapsed",
  Expanded = "expanded",
}

const TASK_ROW_FONT_WEIGHT_BY_HOVER_STATE: Record<GanttLabelHoverState, number> = {
  [GanttLabelHoverState.Hovered]: 600,
  [GanttLabelHoverState.Idle]: 500,
};

const TASK_ROW_COLOR_BY_HOVER_STATE: Record<GanttLabelHoverState, string> = {
  [GanttLabelHoverState.Hovered]: "rgba(255,255,255,0.95)",
  [GanttLabelHoverState.Idle]: "rgba(255,255,255,0.75)",
};

const TASK_ROW_BACKGROUND_BY_HOVER_STATE: Record<GanttLabelHoverState, string> = {
  [GanttLabelHoverState.Hovered]: "rgba(255,255,255,0.06)",
  [GanttLabelHoverState.Idle]: "transparent",
};

const TASK_LABEL_WEIGHT_BY_HOVER_STATE: Record<GanttLabelHoverState, FontWeight> = {
  [GanttLabelHoverState.Hovered]: FontWeight.SemiBold,
  [GanttLabelHoverState.Idle]: FontWeight.Medium,
};

const TASK_ROW_OPACITY_BY_DIM_STATE: Record<GanttLabelDimState, number> = {
  [GanttLabelDimState.Dimmed]: DIMMED_OPACITY,
  [GanttLabelDimState.Normal]: 1,
};

const SECTION_ROW_BACKGROUND_BY_COLLAPSE_STATE: Record<GanttLabelCollapseState, string> = {
  [GanttLabelCollapseState.Collapsed]:
    "linear-gradient(90deg, rgba(102,254,193,0.04) 0%, rgba(102,254,193,0.01) 60%, transparent 100%)",
  [GanttLabelCollapseState.Expanded]:
    "linear-gradient(90deg, rgba(102,254,193,0.10) 0%, rgba(102,254,193,0.03) 60%, transparent 100%)",
};

const SECTION_ROW_STRIPE_OPACITY_BY_COLLAPSE_STATE: Record<GanttLabelCollapseState, number> = {
  [GanttLabelCollapseState.Collapsed]: 0.35,
  [GanttLabelCollapseState.Expanded]: 0.7,
};

const SECTION_ROW_COLOR_BY_COLLAPSE_STATE: Record<GanttLabelCollapseState, string> = {
  [GanttLabelCollapseState.Collapsed]: "rgba(255,255,255,0.55)",
  [GanttLabelCollapseState.Expanded]: "rgba(255,255,255,0.9)",
};

const SECTION_CHEVRON_OPACITY_BY_COLLAPSE_STATE: Record<GanttLabelCollapseState, number> = {
  [GanttLabelCollapseState.Collapsed]: 0.65,
  [GanttLabelCollapseState.Expanded]: 1,
};

const SECTION_CHEVRON_TRANSFORM_BY_COLLAPSE_STATE: Record<GanttLabelCollapseState, string> = {
  [GanttLabelCollapseState.Collapsed]: "rotate(0deg)",
  [GanttLabelCollapseState.Expanded]: "rotate(90deg)",
};

export interface GanttLabelTaskRowModel {
  taskLabel: string;
  fontWeight: number;
  color: string;
  background: string;
  boxShadow?: string;
  opacity: number;
  markdownWeight: FontWeight;
}

export interface GanttLabelSectionRowModel {
  sectionLabel: string;
  background: string;
  boxShadow: string;
  color: string;
  chevronOpacity: number;
  chevronTransform: string;
}

interface SelectGanttLabelTaskRowModelParams {
  taskLayout: GanttTaskLayout;
  interactionState: GanttTaskInteractionState;
}

interface SelectGanttLabelSectionRowModelParams {
  sectionRow: GanttSectionRowLayout;
}

function selectHoverState(isHovered: boolean): GanttLabelHoverState {
  return isHovered ? GanttLabelHoverState.Hovered : GanttLabelHoverState.Idle;
}

function selectDimState(isDimmed: boolean): GanttLabelDimState {
  return isDimmed ? GanttLabelDimState.Dimmed : GanttLabelDimState.Normal;
}

function selectCollapseState(isCollapsed: boolean): GanttLabelCollapseState {
  return isCollapsed
    ? GanttLabelCollapseState.Collapsed
    : GanttLabelCollapseState.Expanded;
}

export function selectGanttLabelTaskRowModel({
  taskLayout,
  interactionState,
}: SelectGanttLabelTaskRowModelParams): GanttLabelTaskRowModel {
  const hoverState = selectHoverState(interactionState.isHovered);
  const dimState = selectDimState(interactionState.isDimmed);
  const statusColor = GANTT_STATUS_COLORS[taskLayout.task.status];
  const boxShadowByHoverState: Record<GanttLabelHoverState, string | undefined> = {
    [GanttLabelHoverState.Hovered]: `inset 3px 0 0 0 ${statusColor}`,
    [GanttLabelHoverState.Idle]: undefined,
  };

  return {
    taskLabel: taskLayout.task.label,
    fontWeight: TASK_ROW_FONT_WEIGHT_BY_HOVER_STATE[hoverState],
    color: TASK_ROW_COLOR_BY_HOVER_STATE[hoverState],
    background: TASK_ROW_BACKGROUND_BY_HOVER_STATE[hoverState],
    boxShadow: boxShadowByHoverState[hoverState],
    opacity: TASK_ROW_OPACITY_BY_DIM_STATE[dimState],
    markdownWeight: TASK_LABEL_WEIGHT_BY_HOVER_STATE[hoverState],
  };
}

export function selectGanttLabelSectionRowModel({
  sectionRow,
}: SelectGanttLabelSectionRowModelParams): GanttLabelSectionRowModel {
  const collapseState = selectCollapseState(sectionRow.isCollapsed);

  return {
    sectionLabel: sectionRow.label,
    background: SECTION_ROW_BACKGROUND_BY_COLLAPSE_STATE[collapseState],
    boxShadow: `inset 2px 0 0 0 rgba(102,254,193,${SECTION_ROW_STRIPE_OPACITY_BY_COLLAPSE_STATE[collapseState]})`,
    color: SECTION_ROW_COLOR_BY_COLLAPSE_STATE[collapseState],
    chevronOpacity: SECTION_CHEVRON_OPACITY_BY_COLLAPSE_STATE[collapseState],
    chevronTransform: SECTION_CHEVRON_TRANSFORM_BY_COLLAPSE_STATE[collapseState],
  };
}
